// Quarter math + date positioning helpers.
import { addDays, differenceInCalendarDays, format, parseISO } from "date-fns";

export interface QuarterRange {
  key: string;        // '2026-Q3'
  label: string;      // 'Q3 2026'
  start: Date;
  end: Date;          // last day of the quarter (inclusive)
  months: { name: string; days: number; offsetDays: number }[];
}

export function quarterRange(key: string): QuarterRange {
  const m = /^(\d{4})-Q([1-4])$/.exec(key);
  if (!m) throw new Error(`bad quarter ${key}`);
  const year = Number(m[1]);
  const q = Number(m[2]);
  const startMonth = (q - 1) * 3; // 0..11
  const start = new Date(year, startMonth, 1);
  const end = new Date(year, startMonth + 3, 0); // last day of month
  const months: QuarterRange["months"] = [];
  let cursor = start;
  let offset = 0;
  for (let i = 0; i < 3; i++) {
    const monthEnd = new Date(year, startMonth + i + 1, 0);
    const days = differenceInCalendarDays(monthEnd, cursor) + 1;
    months.push({
      name: format(cursor, "MMM"),
      days,
      offsetDays: offset,
    });
    offset += days;
    cursor = addDays(monthEnd, 1);
  }
  return {
    key,
    label: `Q${q} ${year}`,
    start,
    end,
    months,
  };
}

export function totalDays(qr: QuarterRange) {
  return differenceInCalendarDays(qr.end, qr.start) + 1;
}

// Returns { leftPct, widthPct } clamped to the quarter range.
export function positionInQuarter(qr: QuarterRange, startISO: string, endISO: string) {
  const total = totalDays(qr);
  const s = parseISO(startISO);
  const e = parseISO(endISO);
  const startOff = Math.max(0, differenceInCalendarDays(s, qr.start));
  const endOff = Math.min(total - 1, differenceInCalendarDays(e, qr.start));
  const span = Math.max(0, endOff - startOff) + 1;
  return {
    leftPct: (startOff / total) * 100,
    widthPct: (span / total) * 100,
  };
}

export function currentQuarterKey(d = new Date()): string {
  const q = Math.floor(d.getMonth() / 3) + 1;
  return `${d.getFullYear()}-Q${q}`;
}

export function nextQuarterKey(d = new Date()): string {
  const q = Math.floor(d.getMonth() / 3) + 1;
  if (q === 4) return `${d.getFullYear() + 1}-Q1`;
  return `${d.getFullYear()}-Q${q + 1}`;
}

export function listQuarters(around = new Date(), pastN = 1, futureN = 2): string[] {
  const out: string[] = [];
  const baseQ = Math.floor(around.getMonth() / 3) + 1;
  const baseY = around.getFullYear();
  for (let i = -pastN; i <= futureN; i++) {
    let q = baseQ + i;
    let y = baseY;
    while (q < 1) { q += 4; y -= 1; }
    while (q > 4) { q -= 4; y += 1; }
    out.push(`${y}-Q${q}`);
  }
  return out;
}

export function fmtDate(iso: string): string {
  return format(parseISO(iso), "MMM d");
}
